import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ArrowLeft, Clock, User } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Blog() {
  usePageMeta({
    title: "Blog, NDIS, Recovery & Social Work Insights",
    description: "Practice insights, plain-language NDIS explainers, athlete wellbeing and community stories from Navica Supports.",
  });
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/posts`);
      setPosts(data || []);
    } catch (e) {
      console.error("Failed to load posts:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <div data-testid="blog-page">
      {/* HERO */}
      <section className="pt-16 lg:pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="text-sm uppercase tracking-[0.2em] text-[#EA823A] font-semibold mb-5 fade-up">From the journal</div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-[#1F2A2E] leading-[1.05] mb-6 fade-up fade-up-1">
            Notes from the <span className="italic" style={{ color: "var(--navica-coral)" }}>field</span>.
          </h1>
          <p className="text-xl text-[#4A595E] leading-relaxed max-w-3xl fade-up fade-up-2">
            Reflections on social work practice, NDIS navigation, advocacy and the everyday wins that move people forward.
          </p>
        </div>
      </section>

      {loading ? (
        <div className="max-w-7xl mx-auto px-5 lg:px-8 pb-24 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {["sk1", "sk2", "sk3"].map((k) => (
            <div key={k} className="h-96 rounded-3xl bg-[#F1E4D5] animate-pulse" />
          ))}
        </div>
      ) : posts.length === 0 ? (
        <div className="max-w-3xl mx-auto px-5 lg:px-8 pb-24 text-center">
          <div className="bg-[#F1E4D5] rounded-[2.5rem] p-12 border border-[#F0E5DE]" data-testid="blog-empty">
            <h3 className="font-display text-2xl text-[#1F2A2E] mb-3">Stories coming soon</h3>
            <p className="text-[#4A595E]">We're putting the finishing touches on our first essays.</p>
          </div>
        </div>
      ) : (
        <>
          {/* FEATURED */}
          <section className="pb-16" data-testid="blog-featured">
            <div className="max-w-7xl mx-auto px-5 lg:px-8">
              <Link to={`/blog/${featured.slug}`} className="grid lg:grid-cols-12 gap-8 group" data-testid={`blog-featured-${featured.slug}`}>
                <div className="lg:col-span-7 aspect-[16/10] rounded-[2.5rem] overflow-hidden">
                  <img
                    src={featured.cover_image || "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?auto=format&fit=crop&w=1200&q=80"}
                    alt={featured.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="lg:col-span-5 flex flex-col justify-center">
                  <div className="text-xs uppercase tracking-[0.2em] text-[#E2718A] font-semibold mb-4">Featured</div>
                  <h2 className="font-display text-3xl lg:text-4xl text-[#1F2A2E] leading-tight mb-5 group-hover:text-[#E2718A] transition-colors">
                    {featured.title}
                  </h2>
                  <p className="text-[#4A595E] leading-relaxed mb-6">{featured.excerpt}</p>
                  <div className="flex items-center gap-5 text-sm text-[#6B7A80] mb-6">
                    <div className="flex items-center gap-2"><User className="w-4 h-4" /> {featured.author}</div>
                    <div className="flex items-center gap-2"><Clock className="w-4 h-4" /> {featured.read_time}</div>
                  </div>
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#E2718A] group-hover:gap-3 transition-all">
                    Read story <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            </div>
          </section>

          {/* GRID */}
          {rest.length > 0 && (
            <section className="pb-24" data-testid="blog-grid">
              <div className="max-w-7xl mx-auto px-5 lg:px-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {rest.map((post, idx) => (
                  <Link key={post.id} to={`/blog/${post.slug}`} className="card-soft group flex flex-col fade-up" style={{ animationDelay: `${idx * 0.08}s` }} data-testid={`blog-card-${post.slug}`}>
                    <div className="aspect-[16/10] rounded-2xl overflow-hidden mb-5">
                      <img
                        src={post.cover_image || "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?auto=format&fit=crop&w=900&q=80"}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                    {post.category && (
                      <span className="text-[10px] uppercase tracking-wider text-[#EA823A] font-semibold mb-2">
                        {post.category}
                      </span>
                    )}
                    <h3 className="font-display text-xl text-[#1F2A2E] mb-3 leading-snug group-hover:text-[#E2718A] transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-sm text-[#4A595E] leading-relaxed mb-4 flex-1">{post.excerpt}</p>
                    <div className="flex items-center justify-between text-xs text-[#6B7A80] pt-4 border-t border-[#F0E5DE]">
                      <span>{post.author}</span>
                      <span>{post.read_time}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  usePageMeta({
    title: post?.title ? `${post.title}, Navica Blog` : "Loading, Navica Blog",
    description: post?.excerpt || "Navica Supports blog, NDIS, recovery and social work insights.",
  });

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/posts/${slug}`);
      setPost(data);
    } catch (e) {
      console.error("Failed to load post:", e);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-8 h-8 border-3 border-[#E2718A] border-t-transparent rounded-full animate-spin" /></div>;
  }
  if (error || !post) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-5">
        <div className="text-center">
          <h1 className="font-display text-3xl text-[#1F2A2E] mb-3">Post not found</h1>
          <Link to="/blog" className="btn-primary mt-4">Back to blog</Link>
        </div>
      </div>
    );
  }

  return (
    <article data-testid="blog-post-page" className="pb-24">
      <div className="max-w-3xl mx-auto px-5 lg:px-8 pt-12">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-[#284952] hover:text-[#E2718A] mb-8 transition" data-testid="back-to-blog">
          <ArrowLeft className="w-4 h-4" /> Back to blog
        </Link>
        {post.category && (
          <div className="text-xs uppercase tracking-[0.2em] text-[#EA823A] font-semibold mb-4">{post.category}</div>
        )}
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#1F2A2E] leading-[1.1] mb-6" data-testid="post-title">
          {post.title}
        </h1>
        <div className="flex items-center gap-5 text-sm text-[#6B7A80] mb-10 pb-10 border-b border-[#F0E5DE]">
          <div className="flex items-center gap-2"><User className="w-4 h-4" /> {post.author}</div>
          <div className="flex items-center gap-2"><Clock className="w-4 h-4" /> {post.read_time}</div>
        </div>
      </div>
      {post.cover_image && (
        <div className="max-w-5xl mx-auto px-5 lg:px-8 mb-12">
          <div className="aspect-[16/9] rounded-[2rem] overflow-hidden">
            <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
          </div>
        </div>
      )}
      <div className="max-w-3xl mx-auto px-5 lg:px-8">
        <div className="prose prose-lg text-[#284952] leading-relaxed" data-testid="post-content">
          {renderPostContent(post.content, slug)}
        </div>
      </div>
    </article>
  );
}

// Render blog content: split by blank lines, and detect markdown-style images
// on their own paragraph, `![alt](url)`, rendering them as a <figure>.
function renderPostContent(raw, slug) {
  const paragraphs = (raw || "").split(/\n\n+/);
  const imgRe = /^!\[([^\]]*)\]\((\S+?)\)\s*$/;
  return paragraphs.map((para, i) => {
    const trimmed = (para || "").trim();
    const m = trimmed.match(imgRe);
    if (m) {
      const [, alt, url] = m;
      return (
        <figure key={`${slug}-p-${i}`} className="my-10" data-testid={`post-image-${i}`}>
          <img
            src={url}
            alt={alt || ""}
            loading="lazy"
            className="w-full rounded-2xl border border-[#F0E5DE]"
          />
          {alt && (
            <figcaption className="text-sm text-[#6B7A80] text-center mt-3 italic">{alt}</figcaption>
          )}
        </figure>
      );
    }
    if (!trimmed) return null;
    return (
      <p key={`${slug}-p-${i}`} className="mb-6 text-lg leading-relaxed whitespace-pre-line">{para}</p>
    );
  });
}
